import type { FocusEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import { formatNumberAndCents, kebabCase } from '@makemydeal/dr-common-utils';
import { setFieldError } from '@makemydeal/dr-dash-store/actions/dealScreenErrorActionCreators';
import {
    setBalloonFallbackHighlight,
    BALLOON_FALLBACK_DEFAULT_AMOUNT
} from '@makemydeal/dr-dash-store/actions/balloonFallbackActionCreators';
import { updateBalloonPaymentAmount } from '@makemydeal/dr-dash-store/actions/offerRedux';
import { getIsUserProgramQuotes, getBalloonAmount } from '@makemydeal/dr-dash-store/selectors/offerRedux';
import { hasBalloonFallbackOccurred } from '@makemydeal/dr-dash-store/selectors/BaseDealSelectors';
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { DealTabs } from '@makemydeal/dr-shared-types';
import { BALLOON } from '@makemydeal/dr-platform-types';
import { getCurrentDealMode } from '@makemydeal/dr-dash-store/selectors/dealModeSelectors';
import { DealMode } from '@makemydeal/dr-dash-store/types/dealModeEnum';

import { dealLockHooks, useUPQRequiredFieldsValidation } from '../../../hooks';
import { convertToNumberOrNull } from '../../../utils/formatUtils';
import { BALLOON_PAYMENT, BALLOON_PAYMENT_REQUIRED_ERROR } from '../../../constants';

export const useBalloonPaymentField = () => {
    const dispatch = useDispatch();
    const fieldKey = kebabCase(BALLOON_PAYMENT);

    const [inputError, setInputError] = useState('');
    const [showBalloonFallbackSnackbar, setShowBalloonFallbackSnackbar] = useState(false);
    const isFocusedRef = useRef(false);
    const focusValueRef = useRef<number | null>(null);

    const isUserProgramQuotes = useSelector(getIsUserProgramQuotes);
    const dealMode = useSelector(getCurrentDealMode);
    const isBaseDealScreenExperience = useSelector(dealerSelectors.isBaseDealExperience);

    const isDealScreenRedDotErrorsEnabled = useSelector(featureToggleSelectors.isDealScreenRedDotErrorsEnabled);
    const isLocked = dealLockHooks.useIsDealLocked();
    const hasBalloonFallback = useSelector(hasBalloonFallbackOccurred);

    const balloonPayment = useSelector(getBalloonAmount);
    const [localValue, setLocalValue] = useState<string>(formatNumberAndCents(balloonPayment));

    // UPQ validation for Balloon
    const { missingFields, hasMissingRequiredFields, lastPaymentMissingUPQFields } = useUPQRequiredFieldsValidation(BALLOON);

    const isBalloonPaymentMissing = useMemo(
        () => hasMissingRequiredFields && missingFields.some((f) => f.field === 'balloonPayment'),
        [hasMissingRequiredFields, missingFields]
    );

    // Handle balloon fallback notification
    useEffect(() => {
        if (hasBalloonFallback) {
            setShowBalloonFallbackSnackbar(true);
            dispatch(setBalloonFallbackHighlight(false));
        }
    }, [hasBalloonFallback, dispatch]);

    useEffect(() => {
        if (!isFocusedRef.current) {
            setLocalValue(formatNumberAndCents(balloonPayment));
        }
    }, [balloonPayment]);

    const handleChange = useCallback((e: InterstateOnChangeEvent<string>) => {
        const value = convertToNumberOrNull(e.target.value);

        if (value === null || value < 0) {
            setInputError('Balloon payment must be $0.00 or greater');
            setLocalValue(e.target.value);
            return;
        }

        setInputError('');
        setLocalValue(e.target.value);
    }, []);

    const handleFocus = useCallback((e: FocusEvent<HTMLInputElement>) => {
        isFocusedRef.current = true;
        focusValueRef.current = convertToNumberOrNull(e.target.value);
    }, []);

    const handleBlur = useCallback(
        (e: InterstateOnChangeEvent<string>) => {
            const blurredValue = convertToNumberOrNull(e.target.value);

            if (focusValueRef.current !== null && blurredValue !== null && blurredValue >= 0) {
                const valueChanged = blurredValue !== focusValueRef.current;
                if (valueChanged) {
                    dispatch(updateBalloonPaymentAmount(blurredValue));
                }
            }

            focusValueRef.current = null;
            isFocusedRef.current = false;
        },
        [dispatch]
    );

    const hasError = Boolean(inputError || (isBalloonPaymentMissing && lastPaymentMissingUPQFields));
    const errorMessage =
        isBalloonPaymentMissing && lastPaymentMissingUPQFields ? BALLOON_PAYMENT_REQUIRED_ERROR : inputError || undefined;

    useEffect(() => {
        if (isDealScreenRedDotErrorsEnabled) {
            dispatch(setFieldError(DealTabs.SUMMARY, fieldKey, hasError));
        }
    }, [fieldKey, hasError, isDealScreenRedDotErrorsEnabled, dispatch]);

    // Display Rule 1 (PQ-Desking Mode): Disable when in Manager View desking mode with PQ (Priced Quote)
    // Note: PQ = !isUserProgramQuotes (UPQ = isUserProgramQuotes)
    const isDisabledByPQDeskingMode = dealMode === DealMode.deskingMode && !isUserProgramQuotes;

    // Display Rule 2 (UPQ-F&I Mode): Enable when in Base Deal Screen with UPQ or F&I mode
    const isEnabledByUPQOrFnIMode = (isBaseDealScreenExperience && isUserProgramQuotes) || dealMode === DealMode.fiMode;

    const isDisabled = isLocked || (isDisabledByPQDeskingMode && !isEnabledByUPQOrFnIMode);

    return {
        label: BALLOON_PAYMENT,
        inputProps: {
            placeholder: 'e.g. 0.00',
            name: fieldKey,
            value: localValue,
            onChange: handleChange,
            onFocus: handleFocus,
            onBlur: handleBlur,
            hasError,
            errorMessage,
            'data-testid': fieldKey,
            allowDecimal: true,
            decimalMaxLength: 2,
            decimalMinLength: 0,
            inputPrefix: '$',
            autoInsertCommas: true,
            size: 'sm' as const,
            disabled: isDisabled
        },
        fallbackSnackbarProps: {
            'data-testid': 'balloon-fallback-snackbar',
            show: showBalloonFallbackSnackbar,
            message: `Balloon Amount has been set to $${formatNumberAndCents(
                BALLOON_FALLBACK_DEFAULT_AMOUNT
            )}. Please update as needed.`,
            position: 'bottom-center' as const,
            onClose: () => setShowBalloonFallbackSnackbar(false),
            type: 'info' as const
        }
    };
};
